import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "payment-status-panel__alert"
}
const _hoisted_2 = { class: "typographies-paragraph-medium-bold payment-status-panel__details" }
const _hoisted_3 = { class: "payment-status-panel__tag" }
const _hoisted_4 = { class: "payment-status-panel__faq" }

import { inject, onMounted, ref, watch } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import Timeline from 'vcomponents/common/timeline/Timeline.vue';
import { TimelineItem } from 'vcomponents/common/timeline/Timeline.type';
import { getPaymentTimeline } from 'api/gocardless/gocardlessService';
import { GocardlessTimelineItemResponse } from 'types/entities/payment/gocardlessDirectdebitTimeline.type';

interface Props {
    directdebitId: string;
    chargeDate: string;
    amount: string;
    directdebitStatus: string;
    paymentState: string;
    nextChargeDate: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PaymentStatusPanel',
  props: {
    directdebitId: {},
    chargeDate: {},
    amount: {},
    directdebitStatus: {},
    paymentState: {},
    nextChargeDate: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $t = inject('$t') as Translator;

const showPanel = ref(false);
const isLoading = ref(true);
const hasFailed = ref(false);
const timelineItems = ref([]);
const translations = ref({
    title: $t.__('document.PaymentStatusPanel.title'),
    fail: $t.__('common.label.fail'),
    apiError: $t.__('document.PaymentStatusPanel.api_error'),
    faq: $t.__('document.PaymentStatusPanel.faq'),
});

function createTimelineItem(item: GocardlessTimelineItemResponse): TimelineItem {
    return {
        icon: getIcon(item.status),
        title: $t.__(`document.PaymentStatusPanel.status.${item.status}.title`),
        label: moment(item.date).format('lll'),
        highlightedDate: getNextChargeDate(item.status),
    };
}

function getIcon(status: string): string {
    const mappingIconStatuses: Record<string, string> = {
        created: 'plus',
        submitted: 'send',
        customer_approval_granted: 'success-circle',
        customer_approval_denied: 'alert-circle',
        confirmed: 'success-circle',
        cancelled: 'alert-circle',
        failed: 'alert-circle',
        charged_back: 'reply',
        paid_out: 'success-circle',
        chargeback_cancelled: 'success-circle',
        late_failure_settled: 'alert-circle',
        chargeback_settled: 'reply',
        resubmission_requested: 'reload',
        surcharge_fee_debited: 'info-circle',
        sds_settlement_delayed: 'warning',
        resubmission_requested_auto: 'gocardless',
    };

    return mappingIconStatuses[status];
}

function getNextChargeDate(status: string): string | null {
    if (status === 'resubmission_requested_auto' && props.nextChargeDate !== '') {
        return props.nextChargeDate;
    }

    return null;
}

function getTagColor(): string {
    switch (props.paymentState) {
        case 'success':
            return CompassEnums.TagColorType.GREEN;
        case 'error':
            return CompassEnums.TagColorType.RED;
        case 'warning':
            return CompassEnums.TagColorType.YELLOW;
        case 'auto_pending':
            return CompassEnums.TagColorType.PURPLE;
        default:
            return CompassEnums.TagColorType.BLUE;
    }
}

function onClose(): void {
    showPanel.value = false;
    emit('close');
}

async function loadData(): Promise<void> {
    try {
        const response = await getPaymentTimeline(props.directdebitId);
        const items = response.data?.data ?? [];
        items.forEach((paymentItem: GocardlessTimelineItemResponse) => {
            timelineItems.value.push(createTimelineItem(paymentItem));
        });
    } catch (error) {
        hasFailed.value = true;
    } finally {
        isLoading.value = false;
    }
}

onMounted(async (): Promise<void> => {
    showPanel.value = true;
    await loadData();
});

watch(
    () => props.directdebitId,
    async () => {
        isLoading.value = true;
        hasFailed.value = false;
        timelineItems.value = [];
        await loadData();
    },
);

return (_ctx: any,_cache: any) => {
  const _component_CompassTag = _resolveComponent("CompassTag")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassLink = _resolveComponent("CompassLink")!
  const _component_CompassPanel = _resolveComponent("CompassPanel")!

  return (_openBlock(), _createBlock(_component_CompassPanel, {
    modelValue: showPanel.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showPanel).value = $event)),
    title: translations.value.title,
    onClosed: onClose
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_CompassLink, {
          label: translations.value.faq,
          link: "https://help.sellsy.com/fr/articles/14960846-comprendre-les-etapes-d-un-prelevement-gocardless",
          target: "_blank",
          type: "secondary",
          prefixIcon: "external-link"
        }, null, 8 /* PROPS */, ["label"])
      ])
    ]),
    default: _withCtx(() => [
      (!hasFailed.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("span", _hoisted_2, _toDisplayString(props.chargeDate) + " - " + _toDisplayString(props.amount), 1 /* TEXT */),
            _createElementVNode("span", _hoisted_3, [
              _createVNode(_component_CompassTag, {
                type: _ctx.CompassEnums.TagType.COLORED,
                color: getTagColor(),
                size: _ctx.CompassEnums.TagSize.SMALL
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(props.directdebitStatus), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["type", "color", "size"])
            ])
          ]))
        : (_openBlock(), _createBlock(_component_CompassAlert, {
            key: 1,
            description: translations.value.apiError,
            type: _ctx.CompassEnums.AlertType.ERROR,
            showIcon: true,
            showClose: false
          }, null, 8 /* PROPS */, ["description", "type"])),
      _createVNode(Timeline, {
        isLoading: isLoading.value,
        items: timelineItems.value
      }, null, 8 /* PROPS */, ["isLoading", "items"])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["modelValue", "title"]))
}
}

})